import type React from 'react'

import type { PortControlProps } from '../types/port-control'

/**
 * True when a focus should paint a ring — i.e. the element was focused by keyboard rather than by
 * a pointer click. `:focus-visible` is the browser's own answer to that question. jsdom does not
 * implement the pseudo-class and throws on `matches`, so under test every focus counts as keyboard
 * focus.
 */
function isKeyboardFocus(el: Element): boolean {
  try {
    return el.matches(':focus-visible')
  } catch {
    return true
  }
}

function handlePortKeyDown(
  e: React.KeyboardEvent<SVGRectElement>,
  control: PortControlProps
): void {
  if (e.key === 'Enter' || e.key === ' ') {
    // Still swallowed when disabled: the canvas binds both keys, and a disabled control must not
    // leak its activation into a canvas shortcut either.
    // `stopPropagation` matters as much as `preventDefault` here: the editor's window-level keydown
    // handler (center-canvas/hooks/use-canvas-keyboard-shortcuts.ts) binds Enter and Space, and its
    // `isEditableTarget` guard matches only real text inputs — `role="button"` is not in that list.
    // Without this, activating a port would also fire a canvas shortcut. React routes
    // `stopPropagation` through to the native event, which is what actually keeps the window
    // listener from seeing it.
    e.preventDefault()
    e.stopPropagation()
    if (!control.disabled) control.onActivateByKeyboard()

    return
  }

  // Only bound while a linking session is live; otherwise Escape falls through to the canvas
  // handler, which deselects.
  if (e.key === 'Escape' && control.onCancelByKeyboard) {
    e.preventDefault()
    e.stopPropagation()
    control.onCancelByKeyboard()
  }
}

/**
 * Attributes that turn a port's transparent hit rect into a real control — or, when the port is
 * decorative, hide it from the accessibility tree so the overlay contributes no noise.
 */
export function portControlAttrs(
  control: PortControlProps | undefined
): React.SVGProps<SVGRectElement> {
  if (!control) return { 'aria-hidden': true }

  return {
    role: 'button',
    tabIndex: control.tabbable ? 0 : -1,
    'aria-label': control.label,
    ...(control.disabled ? { 'aria-disabled': true } : {}),
    onKeyDown: (e) => handlePortKeyDown(e, control),
    onFocus: (e) => control.onFocusChange(isKeyboardFocus(e.currentTarget)),
    onBlur: () => control.onFocusChange(false),
  }
}
